/* eslint-disable no-empty */
interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  type?: string;
}

interface Battery {
  charging: boolean;
  level: number;
  chargingTime: number;
  dischargingTime: number;
}

interface WebGLDebugInfo {
  UNMASKED_VENDOR_WEBGL: number;
  UNMASKED_RENDERER_WEBGL: number;
}

interface NavigatorExtended
  extends Omit<
    Navigator,
    | 'maxTouchPoints'
    | 'webdriver'
    | 'hardwareConcurrency'
    | 'pdfViewerEnabled'
    | 'mediaDevices'
    | 'serviceWorker'
    | 'storage'
  > {
  connection?: NetworkInformation;
  deviceMemory?: number;
  hardwareConcurrency: number;
  maxTouchPoints: number;
  pdfViewerEnabled: boolean;
  webdriver: boolean;
  bluetooth?: { getAvailability(): Promise<boolean> };
  getBattery?: () => Promise<Battery>;
  mediaDevices: MediaDevices;
  storage: StorageManager;
  serviceWorker: ServiceWorkerContainer;
}

interface BrowserInfo {
  // System Information
  userAgent: string;
  platform: string;
  cpuCores: number;
  deviceMemory?: string;
  maxTouchPoints: number;
  batteryStatus?: {
    charging?: boolean;
    level?: number;
    chargingTime?: number;
    dischargingTime?: number;
  };

  // Screen & Window
  screenResolution: string;
  windowSize: string;
  colorDepth: number;
  pixelDepth: number;
  devicePixelRatio: number;
  screenOrientation: string;

  // Browser Capabilities
  cookiesEnabled: boolean;
  doNotTrack: string | null;
  language: string;
  languages: readonly string[];
  vendor: string;
  renderingEngine: string;
  pdfViewerEnabled: boolean;
  webdriver: boolean;

  // Network & Performance
  connectionInfo?: {
    type?: string;
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
  };
  onLine: boolean;

  // Context Information
  timeZone: string;
  referrer: string;
  previousPages: string[];
  permissions: Record<string, string>;

  // Hardware Access
  bluetoothAvailable: boolean;
  webglInfo: {
    vendor: string;
    renderer: string;
    unmaskedVendor: string;
    unmaskedRenderer: string;
  };

  // Additional Privacy Concerns
  mediaDevices?: {
    devices: {
      kind: string;
      label: string;
      deviceId: string;
      groupId: string;
    }[];
    permissions: {
      camera: boolean;
      microphone: boolean;
    };
  };
  credentials?: {
    available: boolean;
    publicKeySupport: boolean;
    biometricSupport: boolean;
  };
  storage?: {
    quota: number;
    usage: number;
    persistent: boolean;
    estimatedRemaining: number;
  };
  serviceWorker?: {
    available: boolean;
    registrations: number;
  };
  sensors?: {
    accelerometer: boolean;
    gyroscope: boolean;
    magnetometer: boolean;
    ambient_light: boolean;
  };
  paymentHandlers?: string[];
  vulnerabilities?: {
    webRTC_leak: boolean;
    canvas_fingerprint: boolean;
    audio_fingerprint: boolean;
    battery_tracking: boolean;
    gamepad_enumeration: boolean;
    vr_devices: boolean;
    clipboard_access: boolean;
    shared_workers: boolean;
    webAssembly: boolean;
    sharedArrayBuffer: boolean;
    performanceAPI: boolean;
  };
  localStorage: {
    available: boolean;
    size: number;
    items: number;
  };
  indexedDB: {
    available: boolean;
    databases?: string[];
  };
  clipboard?: {
    readPermission: string;
    writePermission: string;
    availableFormats?: string[];
    hasContent?: boolean;
    lastModified?: number;
  };
  history?: {
    length: number;
    state: unknown;
    scrollRestoration: ScrollRestoration;
    canGoBack: boolean;
    canGoForward: boolean;
    recentEntries?: {
      url: string;
      title: string;
      lastVisited: number;
    }[];
  };
}

interface IDBFactory {
  databases?(): Promise<{ name: string; version: number }[]>;
}

interface EmailBrowserInfo {
  userAgent: string;
  language: string;
  platform: string;
  screenResolution: string;
  windowSize: string;
  timeZone: string;
  cookiesEnabled: boolean;
  doNotTrack: string | null;
  referrer: string;
  connectionType?: string;
  deviceMemory?: string;
  devicePixelRatio: number;
  vendor: string;
  renderingEngine: string;
}

export const getBrowserInfo = async (): Promise<BrowserInfo> => {
  const nav = window.navigator as unknown as NavigatorExtended;
  const defaultBrowserInfo: BrowserInfo = {
    // System Information
    userAgent: 'unknown',
    platform: 'unknown',
    cpuCores: 1,
    deviceMemory: undefined,
    maxTouchPoints: 0,
    batteryStatus: undefined,

    // Screen & Window
    screenResolution: '0x0',
    windowSize: '0x0',
    colorDepth: 24,
    pixelDepth: 24,
    devicePixelRatio: 1,
    screenOrientation: 'unknown',

    // Browser Capabilities
    cookiesEnabled: false,
    doNotTrack: null,
    language: 'en',
    languages: ['en'],
    vendor: 'unknown',
    renderingEngine: 'unknown',
    pdfViewerEnabled: false,
    webdriver: false,

    // Network & Performance
    connectionInfo: undefined,
    onLine: true,

    // Context Information
    timeZone: 'UTC',
    referrer: '',
    previousPages: [],
    permissions: {},

    // Hardware Access
    bluetoothAvailable: false,
    webglInfo: {
      vendor: 'unknown',
      renderer: 'unknown',
      unmaskedVendor: 'unknown',
      unmaskedRenderer: 'unknown',
    },

    // Additional Privacy Concerns
    mediaDevices: undefined,
    credentials: undefined,
    storage: undefined,
    serviceWorker: undefined,
    sensors: {
      accelerometer: false,
      gyroscope: false,
      magnetometer: false,
      ambient_light: false,
    },
    paymentHandlers: [],
    vulnerabilities: {
      webRTC_leak: false,
      canvas_fingerprint: false,
      audio_fingerprint: false,
      battery_tracking: false,
      gamepad_enumeration: false,
      vr_devices: false,
      clipboard_access: false,
      shared_workers: false,
      webAssembly: false,
      sharedArrayBuffer: false,
      performanceAPI: false,
    },
    localStorage: {
      available: false,
      size: 0,
      items: 0,
    },
    indexedDB: {
      available: false,
      databases: [],
    },
    clipboard: {
      readPermission: 'unknown',
      writePermission: 'unknown',
      availableFormats: [],
      hasContent: false,
    },
    history: {
      length: 0,
      state: null,
      scrollRestoration: 'auto',
      canGoBack: false,
      canGoForward: false,
      recentEntries: [],
    },
  };

  try {
    // Basic system info (most reliable)
    defaultBrowserInfo.userAgent = nav.userAgent;
    defaultBrowserInfo.platform = nav.platform;
    defaultBrowserInfo.cpuCores = nav.hardwareConcurrency;
    defaultBrowserInfo.maxTouchPoints = nav.maxTouchPoints;
    if (nav.deviceMemory) {
      defaultBrowserInfo.deviceMemory = `${nav.deviceMemory}GB`;
    }
  } catch {}

  try {
    // Battery status
    if (nav.getBattery) {
      const battery = await nav.getBattery();
      defaultBrowserInfo.batteryStatus = {
        charging: battery.charging,
        level: battery.level,
        chargingTime: battery.chargingTime,
        dischargingTime: battery.dischargingTime,
      };
    }
  } catch {}

  try {
    // Screen & Window info
    const screen = window.screen;
    defaultBrowserInfo.screenResolution = `${screen.width}x${screen.height}`;
    defaultBrowserInfo.windowSize = `${window.innerWidth}x${window.innerHeight}`;
    defaultBrowserInfo.colorDepth = screen.colorDepth;
    defaultBrowserInfo.pixelDepth = screen.pixelDepth;
    defaultBrowserInfo.devicePixelRatio = window.devicePixelRatio;
    defaultBrowserInfo.screenOrientation = screen.orientation ? screen.orientation.type : 'unknown';
  } catch {}

  try {
    // Browser capabilities
    defaultBrowserInfo.cookiesEnabled = nav.cookieEnabled;
    defaultBrowserInfo.doNotTrack = nav.doNotTrack;
    defaultBrowserInfo.language = nav.language;
    defaultBrowserInfo.languages = nav.languages;
    defaultBrowserInfo.vendor = nav.vendor;
    defaultBrowserInfo.pdfViewerEnabled = nav.pdfViewerEnabled || false;
    defaultBrowserInfo.webdriver = nav.webdriver;

    const ua = nav.userAgent;
    if (ua.includes('Firefox')) defaultBrowserInfo.renderingEngine = 'Gecko';
    else if (ua.includes('Chrome')) defaultBrowserInfo.renderingEngine = 'Blink';
    else if (ua.includes('Safari')) defaultBrowserInfo.renderingEngine = 'WebKit';
    else if (ua.includes('Edge')) defaultBrowserInfo.renderingEngine = 'EdgeHTML';
  } catch {}

  try {
    // Network info
    defaultBrowserInfo.onLine = nav.onLine;
    if (nav.connection) {
      defaultBrowserInfo.connectionInfo = {
        type: nav.connection.type,
        effectiveType: nav.connection.effectiveType,
        downlink: nav.connection.downlink,
        rtt: nav.connection.rtt,
        saveData: nav.connection.saveData,
      };
    }
  } catch {}

  try {
    // WebGL info
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info') as WebGLDebugInfo | null;
      if (debugInfo) {
        defaultBrowserInfo.webglInfo = {
          vendor: gl.getParameter(gl.VENDOR) as string,
          renderer: gl.getParameter(gl.RENDERER) as string,
          unmaskedVendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string,
          unmaskedRenderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string,
        };
      }
    }
  } catch {}

  try {
    // Context info
    defaultBrowserInfo.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    defaultBrowserInfo.referrer = document.referrer;

    if (window.performance?.getEntriesByType) {
      const entries = window.performance.getEntriesByType('navigation');
      defaultBrowserInfo.previousPages = entries.map(
        entry => (entry as PerformanceNavigationTiming).name
      );
    }
  } catch {}

  try {
    // Permissions
    const permissionsToCheck = [
      'geolocation',
      'notifications',
      'microphone',
      'camera',
      'clipboard-read',
      'clipboard-write',
    ] as const;

    for (const permission of permissionsToCheck) {
      try {
        const status = await window.navigator.permissions.query({
          name: permission as PermissionName,
        });
        defaultBrowserInfo.permissions[permission] = status.state;
      } catch {
        defaultBrowserInfo.permissions[permission] = 'unsupported';
      }
    }
  } catch {}

  try {
    // Bluetooth
    defaultBrowserInfo.bluetoothAvailable = !!nav.bluetooth;
  } catch {}

  try {
    // Media Devices
    if (nav.mediaDevices) {
      const devices = await nav.mediaDevices.enumerateDevices();
      defaultBrowserInfo.mediaDevices = {
        devices: devices.map(device => ({
          kind: device.kind,
          label: device.label,
          deviceId: device.deviceId,
          groupId: device.groupId,
        })),
        permissions: {
          camera: devices.some(d => d.kind === 'videoinput' && d.label),
          microphone: devices.some(d => d.kind === 'audioinput' && d.label),
        },
      };
    }
  } catch {}

  try {
    // Credentials API
    if (window.PublicKeyCredential) {
      const platformAuthAvailable =
        (await (
          window.PublicKeyCredential as {
            isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>;
          }
        ).isUserVerifyingPlatformAuthenticatorAvailable?.()) || false;

      defaultBrowserInfo.credentials = {
        available: true,
        publicKeySupport: true,
        biometricSupport: platformAuthAvailable,
      };
    }
  } catch {}

  try {
    // Storage Estimation
    if (nav.storage) {
      const estimate = await nav.storage.estimate();
      const persisted = await nav.storage.persisted();
      defaultBrowserInfo.storage = {
        quota: estimate.quota || 0,
        usage: estimate.usage || 0,
        persistent: persisted,
        estimatedRemaining: (estimate.quota || 0) - (estimate.usage || 0),
      };
    }
  } catch {}

  try {
    // Service Workers
    if (nav.serviceWorker) {
      const registrations = await nav.serviceWorker.getRegistrations();
      defaultBrowserInfo.serviceWorker = {
        available: true,
        registrations: registrations.length,
      };
    }
  } catch {}

  try {
    // Sensor APIs
    const sensors = defaultBrowserInfo.sensors!;
    sensors.accelerometer = 'Accelerometer' in window;
    sensors.gyroscope = 'Gyroscope' in window;
    sensors.magnetometer = 'Magnetometer' in window;
    sensors.ambient_light = 'AmbientLightSensor' in window;
  } catch {}

  try {
    // Payment Handlers
    if ('PaymentRequest' in window) {
      const supportedMethods = [
        'basic-card',
        'https://google.com/pay',
        'https://apple.com/apple-pay',
      ];
      defaultBrowserInfo.paymentHandlers = supportedMethods.filter(method => {
        try {
          new PaymentRequest([{ supportedMethods: method }], {
            total: { label: 'Total', amount: { currency: 'USD', value: '0' } },
          });
          return true;
        } catch {
          return false;
        }
      });
    }
  } catch {}

  try {
    // Vulnerability checks
    const vulns = defaultBrowserInfo.vulnerabilities!;
    vulns.webRTC_leak = 'RTCPeerConnection' in window;
    vulns.canvas_fingerprint = (() => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return false;
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Hello, world!', 0, 0);
      return canvas.toDataURL() !== document.createElement('canvas').toDataURL();
    })();
    vulns.audio_fingerprint = 'AudioContext' in window || 'webkitAudioContext' in window;
    vulns.battery_tracking = 'getBattery' in nav;
    vulns.gamepad_enumeration = 'getGamepads' in nav;
    vulns.vr_devices = 'getVRDisplays' in nav || 'xr' in nav;
    vulns.clipboard_access = nav.clipboard !== undefined;
    vulns.shared_workers = 'SharedWorker' in window;
    vulns.webAssembly = 'WebAssembly' in window;
    vulns.sharedArrayBuffer = 'SharedArrayBuffer' in window;
    vulns.performanceAPI = 'performance' in window && 'memory' in window.performance;
  } catch {}

  try {
    // LocalStorage inspection
    if (typeof window.localStorage !== 'undefined') {
      defaultBrowserInfo.localStorage.available = true;
      defaultBrowserInfo.localStorage.size = new Blob(Object.values(window.localStorage)).size;
      defaultBrowserInfo.localStorage.items = window.localStorage.length;
    }
  } catch {}

  try {
    // IndexedDB inspection
    if (typeof window.indexedDB !== 'undefined') {
      defaultBrowserInfo.indexedDB.available = true;
      if ('databases' in window.indexedDB) {
        const dbs = await (
          window.indexedDB as IDBFactory & { databases(): Promise<{ name: string }[]> }
        ).databases();
        defaultBrowserInfo.indexedDB.databases = dbs.map(db => db.name);
      }
    }
  } catch {}

  try {
    // Clipboard inspection
    const clipboardPermissions = defaultBrowserInfo.permissions;
    defaultBrowserInfo.clipboard = {
      readPermission: clipboardPermissions['clipboard-read'] || 'unknown',
      writePermission: clipboardPermissions['clipboard-write'] || 'unknown',
      availableFormats: [],
      hasContent: false,
    };

    if (nav.clipboard) {
      try {
        // Check for available formats
        const formats = ['text/plain', 'text/html', 'image/png', 'image/jpeg'];
        const availableFormats = [];

        for (const format of formats) {
          try {
            const items = await nav.clipboard.read();
            const hasFormat = items.some(item => item.types.includes(format));
            if (hasFormat) availableFormats.push(format);
          } catch {}
        }

        defaultBrowserInfo.clipboard.availableFormats = availableFormats;
        defaultBrowserInfo.clipboard.hasContent = availableFormats.length > 0;

        // Try to get last modified time if available
        const clipboardItems = await nav.clipboard.read();
        if (clipboardItems?.[0]) {
          defaultBrowserInfo.clipboard.lastModified = Date.now();
        }
      } catch {}
    }
  } catch {}

  try {
    // Browser History inspection
    if (window.history) {
      defaultBrowserInfo.history = {
        length: window.history.length,
        state: window.history.state || null,
        scrollRestoration: window.history.scrollRestoration,
        canGoBack: window.history.length > 1,
        canGoForward: false,
        recentEntries: [],
      };

      // Try to get recent entries if Performance API is available
      if (window.performance?.getEntriesByType) {
        const entries = window.performance.getEntriesByType('navigation');
        const pageEntries = entries.map(entry => ({
          url: (entry as PerformanceNavigationTiming).name,
          title: document.title,
          lastVisited: (entry as PerformanceNavigationTiming).startTime,
        }));

        // Try to get more history using the History API
        if (window.history.state) {
          try {
            const currentIndex = window.history.length - 1;
            const historyEntries = [];

            // Attempt to peek at recent history entries
            for (let i = currentIndex; i >= Math.max(0, currentIndex - 10); i--) {
              if (window.history.state) {
                historyEntries.push({
                  url: window.location.href,
                  title: document.title,
                  lastVisited: Date.now(),
                });
              }
            }

            defaultBrowserInfo.history.recentEntries = [...pageEntries, ...historyEntries];
          } catch {}
        } else {
          defaultBrowserInfo.history.recentEntries = pageEntries;
        }
      }
    }
  } catch {}

  return defaultBrowserInfo;
};

export const getEmailBrowserInfo = async (): Promise<EmailBrowserInfo> => {
  const fullBrowserInfo = await getBrowserInfo();
  return {
    userAgent: fullBrowserInfo.userAgent,
    language: fullBrowserInfo.language,
    platform: fullBrowserInfo.platform,
    screenResolution: fullBrowserInfo.screenResolution,
    windowSize: fullBrowserInfo.windowSize,
    timeZone: fullBrowserInfo.timeZone,
    cookiesEnabled: fullBrowserInfo.cookiesEnabled,
    doNotTrack: fullBrowserInfo.doNotTrack,
    referrer: fullBrowserInfo.referrer,
    connectionType: fullBrowserInfo.connectionInfo?.type,
    deviceMemory: fullBrowserInfo.deviceMemory,
    devicePixelRatio: fullBrowserInfo.devicePixelRatio,
    vendor: fullBrowserInfo.vendor,
    renderingEngine: fullBrowserInfo.renderingEngine,
  };
};
